import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useAuthStore } from '@/core/stores/useAuthStore';
import { useI18n } from '@/hooks/useI18n';
import { useTenantNotifications, type NotificationCallback } from '@/core/stores/useTenantNotifications';
import { fetchUnreadCount } from '@/infrastructure/api/notifications';
import { NotificationPanel } from './NotificationPanel';

export const NotificationBell: React.FC = () => {
  const { t } = useI18n();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<'left' | 'right'>('right');
  const bellRef = useRef<HTMLButtonElement>(null);
  const token = useAuthStore(s => s.token);

  const loadUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const count = await fetchUnreadCount(token);
      setUnreadCount(count);
    } catch (e) {
      console.error('Failed to fetch unread count:', e);
    }
  }, [token]);

  const handleNewNotification: NotificationCallback = useCallback(() => {
    setUnreadCount(prev => prev + 1);
  }, []);

  useTenantNotifications(token || null, handleNewNotification);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  useEffect(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      setPosition(spaceRight > 320 ? 'right' : 'left');
    }
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleNotificationRead = useCallback(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={handleOpen}
        aria-label={t('nav.notifications')}
        className="relative flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        title={t('nav.notifications')}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <NotificationPanel
          position={position}
          onClose={handleClose}
          onNotificationRead={handleNotificationRead}
        />
      )}
    </div>
  );
};
